// Firestore のパス生成を1箇所に集約する。
//
// マルチテナント化（移行フェーズ1）で全データが tenants/{tenantId}/ 配下へ移った。
// 各画面で `tenants/${tenantId}/dishes` と書いて回ると、1箇所書き忘れたときに
// 他テナントのデータへ触れる経路ができる。パスはここでしか組み立てない。
//
// tenantId は Firebase Auth のカスタムクレームから取る（auth.tsx）。
// role や担当品目を Firestore に置いているのは付け替えの即時反映が要るためだが、
// tenantId は利用者の所属が変わらない限り動かないので、クレームで問題ない。
// クレームが無い＝どのテナントにも属していない、として扱う。
import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from "firebase/firestore";

/**
 * テナント配下のパスを組み立てる。
 *
 * tenantId を引数で回すのではなく、この型を通すことで
 * 「テナントを指定し忘れたクエリ」がコンパイル時に書けなくなる。
 */
export interface TenantPaths {
  tenantId: string;
  tenantDoc(): DocumentReference;
  users(): CollectionReference;
  userDoc(uid: string): DocumentReference;
  dishes(): CollectionReference;
  dishDoc(dishId: string): DocumentReference;
  jobs(dishId: string): CollectionReference;
  answers(dishId: string): CollectionReference;
  answerDoc(dishId: string, answerId: string): DocumentReference;
  /** 工程の削除依頼(#174)。文書IDは工程の start_end */
  stepRemovals(dishId: string): CollectionReference;
  stepRemovalDoc(dishId: string, stepKey: string): DocumentReference;
  /** 材料表の追加・削除依頼(#194)。文書IDは op と食材名から */
  ingredientEdits(dishId: string): CollectionReference;
  ingredientEditDoc(dishId: string, key: string): DocumentReference;
  /** マニュアル本体の編集(#203)。文書IDは target:key:field */
  manualEdits(dishId: string): CollectionReference;
  manualEditDoc(dishId: string, key: string): DocumentReference;
  evaluations(): CollectionReference;
  /** 操作の台帳。閲覧の記録(access_logs)ではなく「誰が何を変えたか」 */
  auditLogs(): CollectionReference;
  /** 閲覧の記録(#13)。認可プロキシだけが書き、管理者と料理長が読む */
  accessLogs(): CollectionReference;
  evaluationDoc(evalId: string): DocumentReference;
  /** 食味評価(人が入れる)。評価1件に評価者ごと1文書 */
  tasting(evalId: string): CollectionReference;
  tastingDoc(evalId: string, evaluatorUid: string): DocumentReference;
  quotaDoc(uid: string): DocumentReference;
  /** 管理者による招待（未ログインの人の席）。作成・取り消しは Functions のみ */
  invites(): CollectionReference;
  /** 管理者の同席コード（発行した管理者だけが読める）。書き込みは Functions のみ */
  presenceCodeDoc(code: string): DocumentReference;
  /** 同席中の記録（作業者と品目の組で1つ）。書き込みは Functions のみ */
  presence(): CollectionReference;
}

export function tenantPaths(db: Firestore, tenantId: string): TenantPaths {
  if (!tenantId) throw new Error("tenantId が空です（テナント未所属の利用者）");
  const base = `tenants/${tenantId}`;
  return {
    tenantId,
    tenantDoc: () => doc(db, "tenants", tenantId),
    users: () => collection(db, `${base}/users`),
    userDoc: (uid) => doc(db, `${base}/users`, uid),
    dishes: () => collection(db, `${base}/dishes`),
    dishDoc: (dishId) => doc(db, `${base}/dishes`, dishId),
    jobs: (dishId) => collection(db, `${base}/dishes/${dishId}/jobs`),
    answers: (dishId) => collection(db, `${base}/dishes/${dishId}/answers`),
    answerDoc: (dishId, answerId) => doc(db, `${base}/dishes/${dishId}/answers`, answerId),
    stepRemovals: (dishId) => collection(db, `${base}/dishes/${dishId}/stepRemovals`),
    stepRemovalDoc: (dishId, stepKey) => doc(db, `${base}/dishes/${dishId}/stepRemovals`, stepKey),
    ingredientEdits: (dishId) => collection(db, `${base}/dishes/${dishId}/ingredientEdits`),
    ingredientEditDoc: (dishId, key) => doc(db, `${base}/dishes/${dishId}/ingredientEdits`, key),
    manualEdits: (dishId) => collection(db, `${base}/dishes/${dishId}/manualEdits`),
    manualEditDoc: (dishId, key) => doc(db, `${base}/dishes/${dishId}/manualEdits`, key),
    evaluations: () => collection(db, `${base}/evaluations`),
    auditLogs: () => collection(db, `${base}/audit_logs`),
    accessLogs: () => collection(db, `${base}/access_logs`),
    evaluationDoc: (evalId) => doc(db, `${base}/evaluations`, evalId),
    tasting: (evalId) => collection(db, `${base}/evaluations/${evalId}/tasting`),
    tastingDoc: (evalId, evaluatorUid) => doc(db, `${base}/evaluations/${evalId}/tasting`, evaluatorUid),
    quotaDoc: (uid) => doc(db, `${base}/quotas`, uid),
    invites: () => collection(db, `${base}/invites`),
    presenceCodeDoc: (code) => doc(db, `${base}/presence_codes`, `c${code}`),
    presence: () => collection(db, `${base}/presence`),
  };
}
